import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, BellRing, CheckCircle2, Package, Power, PowerOff, Wrench, type LucideIcon } from 'lucide-react'
import { Button, Separator, cn } from '@asad/crane-ui'

import { DeviceStatusType, MapDevice } from '../types'

/** Kafle KPI: statystyki liczone WYŁĄCZNIE z propsów — bierzemy statusy tylko dla
 *  widocznych urządzeń i redukujemy je jednym przebiegiem. Kafle pełnią też rolę
 *  filtrów: klik przekazuje klucz do widoku. */

/** Klucze filtrów — stabilne, widok mapuje je na swój stan.
 *  Podział na dwie grupy odpowiada dwóm osiom statusu:
 *   - oś `isWorking` → total / working / notWorking,
 *   - oś `alertStatus` → normal / alarm / error / service.
 *  Grupy rozdziela pionowy divider. */
export type KpiKey = 'total' | 'working' | 'notWorking' | 'normal' | 'alarm' | 'error' | 'service'

type KpiAccent = 'neutral' | 'online' | 'offline' | 'alarm' | 'error' | 'service'

/** Techniczny pasek KPI: kolor niesie IKONA (semantyka statusu),
 *  kontener pozostaje neutralny — bez kolorowych krawędzi i plakietek. */
const ACCENT: Record<KpiAccent, { icon: string; ring: string }> = {
  neutral: { icon: 'bg-crane-card-inset text-crane-primary', ring: 'ring-crane-primary/30' },
  online: { icon: 'bg-crane-card-inset text-crane-status-online', ring: 'ring-crane-status-online/30' },
  offline: { icon: 'bg-crane-card-inset text-crane-status-offline', ring: 'ring-crane-status-offline/30' },
  alarm: { icon: 'bg-crane-card-inset text-crane-status-alarm', ring: 'ring-crane-status-alarm/30' },
  error: { icon: 'bg-crane-card-inset text-crane-status-error', ring: 'ring-crane-status-error/30' },
  service: { icon: 'bg-crane-card-inset text-crane-status-service', ring: 'ring-crane-status-service/30' },
}

interface KpiCardProps {
  label: string
  value: number
  accent: KpiAccent
  Icon: LucideIcon
  active?: boolean
  onSelect?: () => void
  className?: string
}

const KpiCard = ({ label, value, accent, Icon, active = false, onSelect, className }: KpiCardProps) => {
  const { t } = useTranslation()
  const tone = ACCENT[accent]

  const base = cn(
    'flex h-full min-w-0 items-center gap-2 rounded-crane-md border-0 bg-crane-card p-2 text-left shadow-crane-soft',
    'md:grid md:content-start md:gap-1.5 md:p-3 md:grid-rows-crane-kpi',
  )

  const content = (
    <>
      {/* `min-w-0`: element siatki bez niego rośnie do pełnej etykiety i wychodzi poza kafel. */}
      <div className='flex min-w-0 items-center gap-2'>
        <span
          className={cn('flex size-crane-icon-tile shrink-0 items-center justify-center rounded-crane-sm', tone.icon)}
        >
          <Icon className='!size-4' aria-hidden />
        </span>
        <span className='hidden truncate text-crane-2xs font-semibold uppercase tracking-crane-label text-crane-muted-foreground md:inline'>
          {label}
        </span>
      </div>
      <div className='text-crane-4xl font-bold leading-crane-stat tabular-nums text-crane-foreground'>{value}</div>
    </>
  )

  if (!onSelect) return <div className={cn(base, className)}>{content}</div>

  return (
    <Button
      type='button'
      variant='ghost'
      className={cn(
        base,
        'cursor-pointer justify-start whitespace-normal font-normal transition-colors hover:bg-crane-card-elevated md:items-stretch md:justify-stretch',
        active && cn('bg-crane-card-elevated ring-1 ring-inset', tone.ring),
        className,
      )}
      aria-pressed={active}
      aria-label={t('common.filterBy', { label })}
      title={t('common.filterBy', { label })}
      onClick={onSelect}
    >
      {content}
    </Button>
  )
}

export interface KpiStripProps {
  visibleDevices: MapDevice[]
  devicesStatuses: DeviceStatusType[]
  selected?: string
  onSelect?: (key: string) => void
}

export const KpiStrip = ({ visibleDevices, devicesStatuses, selected, onSelect }: KpiStripProps) => {
  const { t } = useTranslation()

  const stats = useMemo(() => {
    const visibleIds = new Set(visibleDevices.map(({ id }) => id))
    const visibleStatuses = devicesStatuses.filter(({ id }) => visibleIds.has(id))

    return visibleStatuses.reduce(
      (acc, { isWorking, alertStatus }) => {
        acc.workedDevices += isWorking ? 1 : 0
        acc.stoppedDevices += isWorking ? 0 : 1
        acc.normalDevices += alertStatus === 'normal' ? 1 : 0
        acc.devicesWithAlarms += alertStatus === 'alarm' ? 1 : 0
        acc.devicesWithErrors += alertStatus === 'error' ? 1 : 0
        acc.devicesInService += alertStatus === 'service' ? 1 : 0
        return acc
      },
      {
        workedDevices: 0,
        stoppedDevices: 0,
        normalDevices: 0,
        devicesWithAlarms: 0,
        devicesWithErrors: 0,
        devicesInService: 0,
      },
    )
  }, [visibleDevices, devicesStatuses])

  type Card = { key: KpiKey; label: string; value: number; accent: KpiAccent; Icon: LucideIcon }

  const powerGroup: Card[] = [
    { key: 'total', label: t('device.totalCranes'), value: visibleDevices.length, accent: 'neutral', Icon: Package },
    { key: 'working', label: t('device.operatingCranes'), value: stats.workedDevices, accent: 'online', Icon: Power },
    {
      key: 'notWorking',
      label: t('device.stoppedCranes'),
      value: stats.stoppedDevices,
      accent: 'offline',
      Icon: PowerOff,
    },
  ]

  const alertGroup: Card[] = [
    {
      key: 'normal',
      label: t('device.normalCranes'),
      value: stats.normalDevices,
      accent: 'online',
      Icon: CheckCircle2,
    },
    {
      key: 'alarm',
      label: t('device.maintenanceCranesAlarms'),
      value: stats.devicesWithAlarms,
      accent: 'alarm',
      Icon: BellRing,
    },
    {
      key: 'error',
      label: t('device.maintenanceCranesErrors'),
      value: stats.devicesWithErrors,
      accent: 'error',
      Icon: AlertTriangle,
    },
    {
      key: 'service',
      label: t('device.serviceCranes'),
      value: stats.devicesInService,
      accent: 'service',
      Icon: Wrench,
    },
  ]

  const renderCard = (card: Card) => (
    <KpiCard
      key={card.key}
      label={card.label}
      value={card.value}
      accent={card.accent}
      Icon={card.Icon}
      active={selected === card.key}
      onSelect={onSelect ? () => onSelect(card.key) : undefined}
    />
  )

  return (
    <div className='mb-3 flex flex-col items-stretch gap-2 xl:flex-row'>
      <div className='grid min-w-0 flex-1 gap-2 grid-cols-3'>{powerGroup.map(renderCard)}</div>

      <Separator orientation='vertical' className='hidden h-auto self-stretch bg-crane-border xl:block' />
      <Separator orientation='horizontal' className='bg-crane-border xl:hidden' />

      <div className='grid min-w-0 flex-crane-kpi-wide gap-2 grid-cols-2 md:grid-cols-4'>
        {alertGroup.map(renderCard)}
      </div>
    </div>
  )
}
